import { FillButton } from "PFApp/activities/parts/fill_button";
import { InviteButton } from "PFApp/activities/parts/invite_buttons";
import { FillAndBookButton } from "PFApp/components/fill_and_book_button";
import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { getProfileName, isProfileActive } from "PFCore/helpers/profile";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ButtonLabel from "../../../../activities/parts/button_label";
import AssignButton from "../../../../activities/show/parts/assign_button";
import ExceptionsWarning from "./exceptions_warning";
import css from "./shortlist_item_buttons.module.scss";

const AcceptedButtons = ({
  item,
  activity,
  isResourcer,
  isReviewer,
  isRole,
  qaPrefix,
  hasException,
  isUnbiasedMode,
  template
}) => {
  const { data: currentProfile } = useCurrentProfile();
  const { themeVars } = useTheme();
  const { t } = useTranslation("activities");

  const { mutate: resetShortlist } = useShortlistReset();
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const isAutoBookingCreationEnabled = !!template?.automations?.auto_create_bookings?.enabled;
  const { canProfileCreateBooking } = useBookingModulePermissions();
  const { checkWriteAccessToAtLeastOneCategory } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategory = checkWriteAccessToAtLeastOneCategory({ demandRelated: true });
  const canBook = canProfileCreateBooking(item.profile.id) && hasWriteAccessToAtLeastOneCategory;

  const reviewCompleted = !!activity.review?.completed_at;
  const hasAcceptForEndUser = isPermittedTo(PermissionRule.AcceptForEndUser);
  const [displayConfirm, setDisplayConfirm] = useState(false);
  const isShortlistActive = isProfileActive(item.profile);
  const canFill = activity.state !== "draft" && activity.coowned && isResourcer && isShortlistActive;
  const canInvite =
    activity.state !== "draft" && (activity.coowned || isReviewer) && isShortlistActive && !reviewCompleted;
  const isAssigned = item.state === "assigned";
  const canUndo = !isAssigned && (activity.coowned || isReviewer) && !reviewCompleted;

  const status = activity.coowned ? t("show.shortlist.state.accepted") : t("show.shortlist.state.approved");

  const renderActionButton = () =>
    isAutoBookingCreationEnabled && canBook ? (
      <FillAndBookButton profileId={item.profile.id} shortlistId={item.id} activity={activity} />
    ) : (
      <FillButton qaPrefix={qaPrefix} isUnbiased={isUnbiasedMode} item={item} />
    );

  return (
    <div className={css.rolesActions}>
      {isRole ? (
        template.shortlist_invites && canInvite ? (
          isAssigned && canFill && hasAcceptForEndUser ? (
            renderActionButton()
          ) : (
            <AssignButton item={item} isUnbiasedMode={isUnbiasedMode} />
          )
        ) : canFill ? (
          renderActionButton()
        ) : (
          <>&nbsp;</>
        )
      ) : canInvite ? (
        <InviteButton profile={item.profile} task={activity} />
      ) : (
        <>&nbsp;</>
      )}
      <div className={css.acceptedButtons}>
        {hasException && <ExceptionsWarning />}
        {canUndo && (
          <Button
            text={t("undo")}
            kind="blank"
            qaId={`${qaPrefix}-undo`}
            onClick={() => {
              const isAccepted = item.state === "accepted";
              if (isAccepted) {
                setDisplayConfirm(true);
              } else {
                resetShortlist(item.id);
              }
            }}
          />
        )}
        <ButtonLabel>
          <Icon name="check" size="sm" fill={themeVars["palettePrimaryAction"]} />
          {status}
        </ButtonLabel>
        {displayConfirm && (
          <Modal
            title={t("show.shortlist.removeStatus")}
            labelOK="Confirm" // Avoid "accept" (default) for "unaccepting"
            onOK={() => {
              resetShortlist(item.id);
            }}
            onClose={() => setDisplayConfirm(false)}
            kindOK="danger"
          >
            {t("show.shortlist.removeStatusOnActivity", {
              status: status.toLowerCase(),
              name: getProfileName(item.profile)
            })}
          </Modal>
        )}
      </div>
    </div>
  );
};

AcceptedButtons.propTypes = {
  item: PropTypes.object,
  task: PropTypes.object,
  isResourcer: PropTypes.bool,
  isReviewer: PropTypes.bool,
  isRole: PropTypes.bool,
  qaPrefix: PropTypes.string,
  hasException: PropTypes.bool,
  isUnbiasedMode: PropTypes.bool,
  template: PropTypes.object
};

export default AcceptedButtons;
